// Shared HTTP layer for the Fable + web app. Mirrors the mobile API contract:
// JWT Bearer auth (token in localStorage), same /api backend, friendly errors.
const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fable_web_token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Stable per-browser device id — lets "trust this device" two-step sign-in work on the web too. */
export function deviceId() {
  try {
    let id = localStorage.getItem("fable_web_device");
    if (!id) {
      id = "web-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("fable_web_device", id);
    }
    return id;
  } catch {
    return "web-anon";
  }
}

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

const GENERIC_ERROR = "Something went wrong on our side. Please try again in a moment.";
const TECHNICAL = /traceback|exception|<html|<!doctype|pymongo|objectid|\.py\b/i;
function friendly(msg, fallback) {
  return typeof msg === "string" && msg.trim() && msg.length <= 400 && !TECHNICAL.test(msg) ? msg : fallback;
}
function errorMessage(data, status) {
  const fallback = status >= 500 ? GENERIC_ERROR : "That didn't work. Please check and try again.";
  const detail = data && typeof data === "object" ? data.detail : null;
  if (typeof detail === "string" && detail.trim()) return friendly(detail, fallback);
  if (detail && typeof detail === "object" && typeof detail.message === "string") return friendly(detail.message, fallback);
  if (typeof data === "string" && data.trim() && !/^\s*</.test(data)) return friendly(data, fallback);
  return fallback;
}

/**
 * api(path, { method, body, auth }) — path starts with "/" (no /api prefix).
 */
export async function api(path, opts = {}) {
  const { method = "GET", body, auth = true } = opts;
  const headers = { "Content-Type": "application/json", "X-Platform": "web", "X-Device-Id": deviceId() };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    throw new ApiError(errorMessage(data, res.status), res.status, res.headers.get("x-error-code") || undefined);
  }
  if (data && typeof data === "object" && data.paywall && typeof window !== "undefined") window.dispatchEvent(new CustomEvent("fable:paywall", { detail: data.paywall }));
  return data;
}

export function qs(params) {
  const s = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") s.append(k, String(v));
  });
  return s.toString();
}

// ---- Endpoint groups (web uses the same shared backend as mobile) ----
export const authApi = {
  login: (email, password) => api("/auth/login", { method: "POST", auth: false, body: { email, password } }),
  loginVerify: (challenge_token, code, trust_device = true) => api("/auth/login/verify", { method: "POST", auth: false, body: { challenge_token, code, trust_device } }),
  loginResend: (challenge_token, via) => api("/auth/login/resend", { method: "POST", auth: false, body: via ? { challenge_token, via } : { challenge_token } }),
  register: (payload) => api("/auth/register", { method: "POST", auth: false, body: payload }),
  session: (session_id) => api("/auth/session", { method: "POST", auth: false, body: { session_id } }),
  apple: (identity_token, full_name) => api("/auth/apple", { method: "POST", auth: false, body: { identity_token, full_name } }),
  google: (id_token, nonce) => api("/auth/google", { method: "POST", auth: false, body: { id_token, nonce } }),
  me: () => api("/auth/me"),
  logout: () => api("/auth/logout", { method: "POST" }),
  updateProfile: (data) => api("/auth/me", { method: "PATCH", body: data }),
};

export const listingsApi = {
  list: (params) => api(`/listings?${qs(params)}`, { auth: false }),
  count: (params) => api(`/listings/count?${qs(params)}`, { auth: false }),
  featured: (module) => api(`/listings/featured${module ? `?module=${module}` : ""}`, { auth: false }),
  get: (id) => api(`/listings/${id}`, { auth: false }),
  slots: (id, date, serviceId) =>
    api(`/listings/${id}/slots?date=${date}${serviceId ? `&service_id=${encodeURIComponent(serviceId)}` : ""}`, { auth: false }),
  reviews: (id) => api(`/reviews?listing_id=${id}`, { auth: false }),
  taxonomy: () => api("/taxonomy"),
};

export const bookingsApi = {
  mine: () => api("/bookings"),
  cancel: (id) => api(`/bookings/${id}/cancel`, { method: "POST" }),
  offline: (booking, payment_method = "cash", promo_code) =>
    api("/bookings/offline", { method: "POST", body: { booking, payment_method, promo_code } }),
  stripeSession: (booking, return_base) =>
    api("/payments/stripe/session", { method: "POST", body: { booking, return_base } }),
};

export const favoritesApi = {
  list: () => api("/favorites"),
  toggle: (id) => api(`/favorites/${id}/toggle`, { method: "POST" }),
};

export const appConfigApi = {
  get: () => api("/app-config", { auth: false }),
};
